import { inject } from '@angular/core';
import { CanActivateFn, RedirectCommand, Router } from '@angular/router';
import { AuthenticationService } from '../authentication.service';
import { map, switchMap } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { AuthUrlHelperService } from '../auth-url-helper.service';

export const authnGuard: CanActivateFn = (_route, state) => {
    const authentication = inject(AuthenticationService);
    const authUrlHelper = inject(AuthUrlHelperService);
    const router = inject(Router);

    // Store the original URL the user tried to access
    const returnUrl = state.url;
    const loginPath = router.parseUrl('/login');

    // Extract 'reason' query param if it exists in the original URL
    const parsedUrl = router.parseUrl(returnUrl);
    let reason = parsedUrl.queryParams['reason'];
    const relog = parsedUrl.queryParams['relog'];

    // If not found in query params, check the fragment (in case it's formatted as #reason=...)
    if (!reason && parsedUrl.fragment) {
        const fragmentParams = new URLSearchParams(parsedUrl.fragment);
        reason = fragmentParams.get('reason');
    }

    // Add returnUrl and reason (if present) to login page query params
    loginPath.queryParams = reason ? { returnUrl, reason } : { returnUrl };
    if (relog) {
        loginPath.queryParams = { ...loginPath.queryParams, relog: true };
    }

    try {
        return from(authUrlHelper.getQuerySsoParams(parsedUrl)).pipe(
            switchMap(({ authProvider, idpHint, loginHint, skipLogin }) => {
                if (relog) {
                    return of(false);
                }
                return from(authentication.initialize(authProvider, idpHint, loginHint, { skipLogin }));
            }),
            switchMap(init => {
                if (!init) {
                    return of(new RedirectCommand(loginPath));
                }
                try {
                    return authentication.checkLoggedIn$().pipe(
                        map(isAuthenticated => {
                            if (!isAuthenticated) {
                                return new RedirectCommand(loginPath);
                            }
                            return isAuthenticated;
                        })
                    );
                } catch (e) {
                    return of(new RedirectCommand(loginPath));
                }
            })
        );
    } catch (e) {
        return new RedirectCommand(loginPath);
    }
};
